import { FC, useEffect, useState, useRef, useCallback } from "react"
import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  makeStyles,
  Typography,
} from "@material-ui/core"
import parsePhoneNumber from "libphonenumber-js"
import { useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import AuthCode, { AuthCodeRef } from "react-auth-code-input"
import { useApi } from "../../utils/API"
import { colors } from "../../utils/theme"
import { DefaultCountry } from "../../utils/constants"
import { GhostButton, PrimaryButton } from "../atoms/button/Button"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useUpdateUserPhoneNumberMutation } from "../../generated/graphql"
import { Query } from "../../utils/constants"

interface MobileVerifyDialogProps extends DialogProps {
  mobile: string
  userId: string
  openMobileUpdateDialog: () => void
  verifySuccess: () => void
  closeCallback: () => void
  cancelButtonLabel?: string
}

const useStyles = makeStyles((theme) => ({
  title: {
    padding: theme.spacing(4, 4, 2),
  },
  titleContent: {
    fontSize: 30,
    color: colors.neutral.denim,
  },
  contentRoot: {
    padding: theme.spacing(1, 4, 4),
  },
  mobileContent: {
    color: colors.neutral.denim,
    display: "inline",
  },
  numberInput: {
    width: 46,
    height: 61,
    padding: 0,
    fontSize: 24,
    textAlign: "center",
    marginRight: theme.spacing(2),
    color: colors.neutral.denim,
    border: "none",
    borderRadius: 4,
    backgroundColor: colors.primary.cloud_05,
  },
  inputContainer: {
    width: 379,
    textAlign: "center",
    marginTop: theme.spacing(8),
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ghostButton: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(10),
    "& button": {
      fontSize: 20,
    },
  },
  primaryButton: {
    marginBottom: theme.spacing(2),
  },
  button: {
    width: "100%",
    fontSize: 20,
  },
  errorContent: {
    color: `${colors.secondary.crimson} !important`,
    marginTop: theme.spacing(2),
  },
  closeButton: {
    marginTop: theme.spacing(5),
  },
}))

const COUNTDOWNNUMBER = 30

const MobileVerifyDialog: FC<MobileVerifyDialogProps> = ({
  mobile,
  userId,
  openMobileUpdateDialog,
  verifySuccess,
  closeCallback,
  cancelButtonLabel = "Do not update mobile number",
  ...props
}) => {
  const API = useApi()
  const classes = useStyles()
  const queryClient = useQueryClient()
  const graphQLClient = useGraphQLClient()
  const [code, setCode] = useState<string>("")
  const codeInputRef = useRef<AuthCodeRef>(null)
  const [timing, setTiming] = useState<number>(COUNTDOWNNUMBER)
  const [timeInterval, setTimeInterval] = useState<NodeJS.Timeout | null>(null)
  const onSuccess = () =>
    queryClient.invalidateQueries({ queryKey: [Query.GET_PRACTITIONER] })
  const [hasError, setHasError] = useState<boolean>(false)
  const [twilioPhoneNumber, setTwilioPhoneNumber] = useState<string>("")

  const { mutate: updateUserPhoneNumber } = useUpdateUserPhoneNumberMutation(
    graphQLClient,
    {
      onSuccess,
    }
  )

  const getSMSCode = useCallback(
    async (mobile: string) => {
      return await API.sendSmsVerificationCode(mobile)
    },
    [API]
  )

  const verifySMSCode = useCallback(
    async (code: string, mobile: string) => {
      return await API.verifySmsCode(mobile, code)
    },
    [API]
  )

  const resendCode = () => {
    codeInputRef.current?.clear()
    setTiming(COUNTDOWNNUMBER)
    const interval = countDownInterval()
    setTimeInterval(interval)
    // Call the endpoint to resend the code
    setHasError(false)
    getSMSCode(twilioPhoneNumber)
  }

  const editNumber = () => {
    if (timeInterval) {
      clearInterval(timeInterval)
      setTimeInterval(null)
      setTiming(COUNTDOWNNUMBER)
    }
    setHasError(false)
    openMobileUpdateDialog()
  }

  const countDownInterval = () => {
    const interval = setInterval(() => {
      setTiming((timing) => Number(timing) - 1)
    }, 1000)
    return interval
  }

  const submit = async () => {
    if (code.length === 6) {
      setHasError(false)
      const result = await verifySMSCode(code, twilioPhoneNumber)
      if (result.data.success) {
        updateUserPhoneNumber({ userId, mobile: twilioPhoneNumber })
        verifySuccess()
      } else {
        setHasError(true)
      }
    }
  }

  const handleOnChange = async (code: string) => {
    setCode(code)
  }

  useEffect(() => {
    if (props.open) {
      const phoneNumber = parsePhoneNumber(mobile, DefaultCountry)
      if (phoneNumber) {
        setTwilioPhoneNumber(phoneNumber.number.toString())
        getSMSCode(phoneNumber.number.toString())
      }
    }
  }, [getSMSCode, mobile, props.open])

  useEffect(() => {
    if (props.open) {
      const interval = countDownInterval()
      setTimeInterval(interval)
    }
  }, [props.open])

  useEffect(() => {
    if (timeInterval && timing === 0) {
      clearInterval(timeInterval)
    }
  }, [timeInterval, timing])

  return (
    <Dialog {...props} fullWidth maxWidth="xs">
      <DialogTitle disableTypography className={classes.title}>
        <Typography
          variant="h3"
          component="h2"
          className={classes.titleContent}
        >
          Verify your mobile number
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.contentRoot}>
        <Typography variant="body1" className={classes.mobileContent}>
          Enter the six digit code sent to
        </Typography>{" "}
        <Typography variant="h6" className={classes.mobileContent}>
          {mobile}
        </Typography>
        <div className={classes.inputContainer}>
          <AuthCode
            allowedCharacters="numeric"
            onChange={handleOnChange}
            inputClassName={classes.numberInput}
            ref={codeInputRef}
          />
          <div className={classes.errorContent} hidden={!hasError}>
            Invalid verification code. Please try again.
          </div>
        </div>
        <div className={clsx(classes.ghostButton, classes.center)}>
          <GhostButton disabled={timing !== 0} onClick={resendCode}>
            Resend Code {timing !== 0 ? timing : ""}
          </GhostButton>
        </div>
        <div className={clsx(classes.primaryButton, classes.center)}>
          <PrimaryButton onClick={submit} className={classes.button}>
            Submit
          </PrimaryButton>
        </div>
        <div className={classes.center}>
          <GhostButton onClick={editNumber}>Edit Number</GhostButton>
        </div>
        <div className={clsx(classes.closeButton, classes.center)}>
          <GhostButton onClick={closeCallback}>{cancelButtonLabel}</GhostButton>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default MobileVerifyDialog
